import { OutlinedTextFieldProps, StandardTextFieldProps } from '@mui/material';
import React from 'react';
import { useCustomTranslations } from '../../../../hooks/useCustomTranslations';
import { RegistrationField } from '../../../../interfaces/interfaces';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import RegistrationCheckbox from './RegistrationCheckbox';
import RegistrationEmailField from './RegistrationEmailField';
import RegistrationLanguageField from './RegistrationLanguageField';
import RegistrationNumberField from './RegistrationNumberField';
import RegistrationPhoneNumber from './RegistrationPhoneNumber';
import RegistrationSelect from './RegistrationSelect';
import RegistrationTextField from './RegistrationTextField';
import RegistrationFileField from './RegistrationFileField';
import { useTranslation } from 'next-i18next';

export interface RegistrationFormFieldProps {
  field: RegistrationField;
  requiredFieldIds: Map<string, string[]>;
  updateRequiredFieldIds: (requiredIds: Map<string, string[]>) => void;
  disabled?: boolean;
  defaultValue?: string;
  variant?: StandardTextFieldProps['variant'] | OutlinedTextFieldProps['variant'];
  limitedValuesCount?: { _id: string; value: string; count: number }[];
  color?: string;
  checkboxColor?: string;
  fieldsTextColor?: string;
  fieldsBorderColor?: string;
  selectFieldSelectedColor?: string;
  clickableFiles?: boolean;
  isRequiredProp?: boolean;
  withOAuth?: boolean;
  isGuestInviteForm?: boolean;
  showChild?: boolean;
  showFieldLabel?: boolean;
  skipUserAlreadyRegisteredCheck?: boolean;
  limitedFieldsLoaded?: boolean;
  labelColor?: string;
  textFieldSx?: any;
  allowEmptyEmail?: boolean;
  skipRequired?: boolean;
  showEmailConfirmation?: boolean;
  onChange?: (key: string, value: string) => void;
  onBlur?: (key: string, value: string) => void;
  hideGeneratedEmail?: boolean;
}

export const RegistrationFormField = (props: RegistrationFormFieldProps) => {
  const {
    field,
    requiredFieldIds,
    updateRequiredFieldIds,
    defaultValue,
    variant = 'outlined',
    disabled = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    color,
    fieldsTextColor,
    selectFieldSelectedColor,
    fieldsBorderColor,
    limitedValuesCount,
    clickableFiles = false,
    isRequiredProp = false,
    withOAuth = false,
    isGuestInviteForm = false,
    showChild = true,
    skipUserAlreadyRegisteredCheck,
    limitedFieldsLoaded,
    labelColor,
    textFieldSx,
    allowEmptyEmail = false,
    skipRequired = false,
    showEmailConfirmation,
    onChange,
    onBlur,
    hideGeneratedEmail = false,
  } = props;

  const event = useSelector((state) => state.common.event ?? state.admin.event);

  const { ct } = useCustomTranslations();
  const { t } = useTranslation();

  const helperText = field.helperText ? ct(field.helperText, field, 'helperTextTranslations') : '';
  const isSelect = field.textValueOptions?.length > 0 && (field.fieldType == 'text' || field.fieldType == 'limited' || field.fieldType == 'salutation');
  const isEmail = field.fieldType === 'text' && field.isEmailField;
  const showFieldLabel = false;
  const skipEmail = allowEmptyEmail || (event?.modules?.guestmanagement?.settings?.authWithoutEmail && skipRequired);

  const isRequired = (field: RegistrationField) => {
    const isFieldInRequiredIds = Array.from(requiredFieldIds.values()).flat().includes(field._id);

    const baseRequirement = isRequiredProp || field.isRequired || isFieldInRequiredIds;

    return isEmail ? baseRequirement : baseRequirement && !skipRequired;
  };

  const showLabelRequired = () => {
    if ((isEmail && skipEmail) || (!isEmail && skipRequired)) {
      return false;
    } else if (field.isRequired || isRequired(field)) {
      return true;
    }
    return false;
  };

  if (!showChild) return null;

  return (
    <div className="my-3" key={`registration-field-${field._id}`} id={field._id}>
      {field.fieldType !== 'checkbox' && (
        <div className={`${labelColor} text-base font-semibold`}>
          {ct(field.label, field, 'labelTranslations')}
          {showLabelRequired() && '*'}
        </div>
      )}
      {isSelect && (
        <RegistrationSelect
          field={field}
          defaultValue={defaultValue}
          disabled={disabled}
          fieldsTextColor={fieldsTextColor}
          fieldsBorderColor={fieldsBorderColor}
          selectFieldSelectedColor={selectFieldSelectedColor}
          textFieldSx={textFieldSx}
          isRequired={isRequired}
          variant={variant}
          requiredFieldIds={requiredFieldIds}
          limitedValuesCount={limitedValuesCount}
          limitedFieldsLoaded={limitedFieldsLoaded}
          updateRequiredFieldIds={updateRequiredFieldIds}
          showFieldLabel={showFieldLabel}
          onChange={onChange}
          onBlur={onBlur}
        />
      )}
      {!isSelect && (
        <>
          {field.fieldType === 'checkbox' && (
            <RegistrationCheckbox
              field={field}
              defaultValue={defaultValue}
              disabled={disabled}
              isRequired={isRequired}
              color={color}
              showFieldLabel={showFieldLabel}
              checkboxColor={fieldsBorderColor}
              onChange={(key, value) => {
                onChange?.(key, value);
                onBlur?.(key, value);
              }}
            />
          )}

          {field.fieldType === 'language' && (
            <RegistrationLanguageField
              field={field}
              defaultValue={defaultValue}
              disabled={disabled}
              fieldsTextColor={fieldsTextColor}
              fieldsBorderColor={fieldsBorderColor}
              textFieldSx={textFieldSx}
              isRequired={isRequired}
              variant={variant}
              requiredFieldIds={requiredFieldIds}
              updateRequiredFieldIds={updateRequiredFieldIds}
              showFieldLabel={showFieldLabel}
              onChange={onChange}
              onBlur={onBlur}
            />
          )}

          {field.fieldType === 'number' && (
            <RegistrationNumberField
              field={field}
              defaultValue={defaultValue}
              disabled={disabled}
              fieldsTextColor={fieldsTextColor}
              fieldsBorderColor={fieldsBorderColor}
              textFieldSx={textFieldSx}
              isRequired={isRequired}
              variant={variant}
              requiredFieldIds={requiredFieldIds}
              showFieldLabel={showFieldLabel}
              onChange={onChange}
              onBlur={onBlur}
            />
          )}

          {field.fieldType === 'text' && !field.isEmailField && (
            <RegistrationTextField
              field={field}
              defaultValue={defaultValue}
              disabled={disabled}
              fieldsTextColor={fieldsTextColor}
              fieldsBorderColor={fieldsBorderColor}
              textFieldSx={textFieldSx}
              isRequired={isRequired}
              variant={variant}
              showFieldLabel={showFieldLabel}
              onValueChanged={onChange}
              onBlur={onBlur}
            />
          )}

          {isEmail && (
            <RegistrationEmailField
              variant={variant}
              defaultValue={defaultValue}
              field={field}
              withOAuth={withOAuth}
              disabled={disabled}
              fieldsTextColor={fieldsTextColor}
              fieldsBorderColor={fieldsBorderColor}
              textFieldSx={textFieldSx}
              isGuestInviteForm={isGuestInviteForm}
              showFieldLabel={showFieldLabel}
              skipUserAlreadyRegisteredCheck={skipUserAlreadyRegisteredCheck}
              onChange={onChange}
              allowEmptyEmail={skipEmail}
              showEmailConfirmation={showEmailConfirmation}
              onBlur={onBlur}
              hideGeneratedEmail={hideGeneratedEmail}
            />
          )}
          {field.fieldType === 'phonenumber' && (
            <RegistrationPhoneNumber
              field={field}
              disabled={disabled}
              defaultValue={defaultValue}
              fieldsBorderColor={fieldsBorderColor}
              fieldsTextColor={fieldsTextColor}
              isRequired={isRequired}
              showFieldLabel={showFieldLabel}
              onChange={onChange}
              onBlur={onBlur}
            />
          )}
          {field.fieldType === 'file' && (
            <RegistrationFileField
              field={field}
              disabled={disabled}
              clickableFiles={clickableFiles}
              color={fieldsTextColor}
              fieldsBorderColor={fieldsBorderColor}
              isRequired={isRequired}
              showFieldLabel={showFieldLabel}
              onChange={onChange}
              onBlur={onBlur}
              defaultValue={defaultValue}
            />
          )}
          {field.fieldType === 'multi-files' && (
            <RegistrationFileField
              uploadMultiple
              field={field}
              disabled={disabled}
              clickableFiles={clickableFiles}
              color={fieldsTextColor}
              fieldsBorderColor={fieldsBorderColor}
              isRequired={isRequired}
              showFieldLabel={showFieldLabel}
              onChange={onChange}
              onBlur={onBlur}
              defaultValue={defaultValue}
            />
          )}
        </>
      )}
      {helperText && (
        <div
          className="text-base"
          style={{ color: fieldsTextColor, maxWidth: '100%', wordWrap: 'break-word' }}
          dangerouslySetInnerHTML={{ __html: ct(helperText, field, 'helperTextTranslations') || '' }}
        />
      )}
    </div>
  );
};
